import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertTriangle, Loader2, FileText, RefreshCw, Settings, Clock, Zap, ToggleLeft, ToggleRight, Link2, XCircle, Webhook, Copy, RotateCw, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../utils/api';

const DMS_SYSTEMS = [
  { id: 'generic', name: 'Generic CSV', desc: 'Standard CSV format' },
  { id: 'titletec', name: 'Title Tec', desc: 'Title Tec DMS export' },
  { id: 'dealertrack', name: 'DealerTrack', desc: 'DealerTrack inventory export' },
  { id: 'frazer', name: 'Frazer', desc: 'Frazer DMS export' },
  { id: 'vauto', name: 'vAuto', desc: 'vAuto inventory file' },
];

export default function InventorySync() {
  const [activeView, setActiveView] = useState('import-export');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedDms, setSelectedDms] = useState('generic');
  const fileRef = useRef(null);

  // Auto-sync state
  const [syncConfig, setSyncConfig] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  // Webhook state
  const [webhookConfig, setWebhookConfig] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const fetchSyncConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/api/inventory/sync/config');
      setSyncConfig(data);
    } catch {}
  }, []);

  const fetchSyncHistory = useCallback(async () => {
    try {
      const { data } = await api.get('/api/inventory/sync/history');
      setSyncHistory(data);
    } catch {}
  }, []);

  const fetchWebhookConfig = useCallback(async () => {
    try {
      const { data } = await api.get('/api/inventory/webhook/config');
      setWebhookConfig(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (activeView === 'auto-sync') {
      setSyncLoading(true);
      Promise.all([fetchSyncConfig(), fetchSyncHistory()]).finally(() => setSyncLoading(false));
    }
    if (activeView === 'webhook') {
      setWebhookLoading(true);
      Promise.all([fetchWebhookConfig(), fetchSyncHistory()]).finally(() => setWebhookLoading(false));
    }
  }, [activeView, fetchSyncConfig, fetchSyncHistory, fetchWebhookConfig]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/api/inventory/export', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `wakzy_inventory_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Inventory exported successfully');
    } catch { toast.error('Failed to export inventory'); }
    finally { setExporting(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(`/api/inventory/import/template?dms_profile=${selectedDms}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'wakzy_import_template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download template'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv') && !file.name.toLowerCase().endsWith('.txt')) {
      toast.error('Please upload a CSV file');
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/api/inventory/import?dms_profile=${selectedDms}`, formData);
      setImportResult(data);
      if (data.imported > 0) toast.success(data.message);
      else toast.error('No vehicles imported. Check your file format.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to import'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleUpdateSyncConfig = async (updates) => {
    setSavingConfig(true);
    try {
      const { data } = await api.put('/api/inventory/sync/config', updates);
      setSyncConfig(data);
      toast.success('Sync configuration updated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setSavingConfig(false); }
  };

  const handleTriggerSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post('/api/inventory/sync/trigger');
      toast.success(data.imported > 0 ? `Synced ${data.imported} vehicles` : 'Sync completed — no new vehicles');
      fetchSyncConfig();
      fetchSyncHistory();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
    finally { setSyncing(false); }
  };

  const handleRegenerateWebhook = async () => {
    if (!window.confirm('Regenerate webhook key? The old URL will stop working immediately.')) return;
    setRegenerating(true);
    try {
      const { data } = await api.post('/api/inventory/webhook/regenerate');
      setWebhookConfig(prev => ({ ...prev, ...data }));
      toast.success('Webhook key regenerated');
    } catch { toast.error('Failed to regenerate'); }
    finally { setRegenerating(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success('Copied to clipboard'));
  };

  return (
    <div data-testid="inventory-sync">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center justify-center shadow-lg">
          <FileSpreadsheet className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-gray-900 text-xl">Import / Export Inventory</h3>
          <p className="text-gray-500">Sync with Title Tec, DealerTrack, Frazer, vAuto &amp; more</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {[
          { id: 'import-export', label: 'Manual Import/Export', icon: FileSpreadsheet },
          { id: 'auto-sync', label: 'Auto Sync', icon: RefreshCw },
          { id: 'webhook', label: 'Webhook', icon: Webhook },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveView(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeView === tab.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`sync-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* ===== MANUAL IMPORT/EXPORT ===== */}
      {activeView === 'import-export' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Import Section */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-indigo-500 to-blue-500">
              <h4 className="font-bold text-white flex items-center gap-2"><Upload className="w-5 h-5" /> Import Inventory</h4>
              <p className="text-indigo-100 text-sm mt-1">Upload a CSV from your dealer software</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Source Software</label>
                <Select value={selectedDms} onValueChange={setSelectedDms}>
                  <SelectTrigger className="rounded-xl" data-testid="dms-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DMS_SYSTEMS.map(d => (
                      <SelectItem key={d.id} value={d.id}>
                        <span className="flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-gray-400" />{d.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:border-indigo-300 transition-colors">
                <input ref={fileRef} type="file" accept=".csv,.txt" className="hidden" onChange={handleImport} data-testid="import-file-input" />
                <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="rounded-xl" data-testid="import-upload-btn">
                  {importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : 'Choose CSV File'}
                </Button>
                <p className="text-xs text-gray-400 mt-2">CSV or TXT up to 10MB</p>
              </div>
              <Button variant="outline" size="sm" className="rounded-xl text-indigo-600" onClick={handleDownloadTemplate} data-testid="download-template-btn">
                <Download className="w-3.5 h-3.5 mr-1.5" />Download Template
              </Button>
              {importResult && (
                <div className={`rounded-xl p-4 ${importResult.imported > 0 ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="import-result">
                  <div className="flex items-center gap-2 mb-2">
                    {importResult.imported > 0 ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                    <span className="font-medium text-gray-900">{importResult.message}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center mt-3">
                    <div className="bg-white rounded-lg p-2"><p className="text-lg font-bold text-green-600">{importResult.imported}</p><p className="text-xs text-gray-500">Imported</p></div>
                    <div className="bg-white rounded-lg p-2"><p className="text-lg font-bold text-amber-600">{importResult.duplicates}</p><p className="text-xs text-gray-500">Duplicates</p></div>
                    <div className="bg-white rounded-lg p-2"><p className="text-lg font-bold text-gray-600">{importResult.skipped}</p><p className="text-xs text-gray-500">Skipped</p></div>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <div className="mt-3 text-xs text-red-600 space-y-1">{importResult.errors.map((err, i) => <p key={i}>{err}</p>)}</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Export Section */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 to-teal-500">
              <h4 className="font-bold text-white flex items-center gap-2"><Download className="w-5 h-5" /> Export Inventory</h4>
              <p className="text-emerald-100 text-sm mt-1">Download your inventory as CSV</p>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">Export all your vehicles to a standard CSV file compatible with any dealer management system.</p>
              <Button onClick={handleExport} disabled={exporting} className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white h-12" data-testid="export-btn">
                {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</> : <><Download className="w-4 h-4 mr-2" />Export All Vehicles</>}
              </Button>
              <div className="border-t border-gray-100 pt-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-3">Compatible With</h5>
                <div className="grid grid-cols-2 gap-2">
                  {DMS_SYSTEMS.map(d => (
                    <div key={d.id} className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />{d.name}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== AUTO SYNC ===== */}
      {activeView === 'auto-sync' && (
        <div className="space-y-6">
          {syncLoading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* Config Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-white flex items-center gap-2"><Settings className="w-5 h-5" /> Sync Configuration</h4>
                    <p className="text-violet-100 text-sm mt-1">Set up automatic inventory syncing from your DMS</p>
                  </div>
                  <button
                    onClick={() => handleUpdateSyncConfig({ enabled: !syncConfig?.enabled })}
                    className="flex items-center gap-2 text-white"
                    data-testid="sync-toggle"
                  >
                    {syncConfig?.enabled ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8 opacity-60" />}
                    <span className="text-sm font-medium">{syncConfig?.enabled ? 'Active' : 'Off'}</span>
                  </button>
                </div>
                <div className="p-6 space-y-5">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">DMS Software</label>
                      <Select value={syncConfig?.dms_profile || 'generic'} onValueChange={v => handleUpdateSyncConfig({ dms_profile: v })}>
                        <SelectTrigger className="rounded-xl" data-testid="sync-dms-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DMS_SYSTEMS.map(d => (
                            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Sync Frequency</label>
                      <Select value={syncConfig?.schedule || 'daily'} onValueChange={v => handleUpdateSyncConfig({ schedule: v })}>
                        <SelectTrigger className="rounded-xl" data-testid="sync-schedule-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Every Hour</SelectItem>
                          <SelectItem value="every_6h">Every 6 Hours</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                      <Link2 className="w-3.5 h-3.5 inline mr-1" />CSV Feed URL
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={syncConfig?.sync_url || ''}
                        onChange={e => setSyncConfig(prev => ({ ...prev, sync_url: e.target.value }))}
                        placeholder="https://your-dms.com/export/inventory.csv"
                        className="rounded-xl flex-1"
                        data-testid="sync-url-input"
                      />
                      <Button
                        variant="outline"
                        className="rounded-xl"
                        disabled={savingConfig}
                        onClick={() => handleUpdateSyncConfig({ sync_url: syncConfig?.sync_url || '' })}
                        data-testid="save-sync-url-btn"
                      >
                        Save
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Enter the URL where your DMS exports inventory as CSV. Wakzy will auto-fetch on schedule.
                    </p>
                  </div>

                  {/* Last Sync Status */}
                  {syncConfig?.last_sync_at && (
                    <div className={`rounded-xl p-4 flex items-center justify-between ${
                      syncConfig.last_sync_status === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`} data-testid="last-sync-status">
                      <div className="flex items-center gap-3">
                        {syncConfig.last_sync_status === 'success' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Last sync: {new Date(syncConfig.last_sync_at).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            {syncConfig.last_sync_imported} vehicles imported
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleTriggerSync}
                    disabled={syncing || !syncConfig?.sync_url}
                    className="rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                    data-testid="trigger-sync-btn"
                  >
                    {syncing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</> : <><Zap className="w-4 h-4 mr-2" />Sync Now</>}
                  </Button>
                </div>
              </div>

              {/* Sync History */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2"><Clock className="w-5 h-5 text-gray-400" /> Sync History</h4>
                  <Button variant="ghost" size="sm" className="rounded-xl" onClick={fetchSyncHistory} data-testid="refresh-history-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
                  </Button>
                </div>
                <div className="p-4">
                  {syncHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Clock className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p className="font-medium">No sync history yet</p>
                      <p className="text-sm">Configure auto-sync or trigger a manual sync to get started.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {syncHistory.map((h, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3" data-testid={`sync-history-${i}`}>
                          <div className="flex items-center gap-3">
                            {h.status === 'success' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {h.imported} imported, {h.duplicates} duplicates, {h.skipped} skipped
                              </p>
                              <p className="text-xs text-gray-400">
                                {h.started_at ? new Date(h.started_at).toLocaleString() : '-'}
                                {h.manual && <span className="ml-2 text-indigo-500">Manual</span>}
                              </p>
                            </div>
                          </div>
                          {h.errors?.length > 0 && (
                            <span className="text-xs text-red-500">{h.errors.length} error{h.errors.length > 1 ? 's' : ''}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Setup Guide */}
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl border border-indigo-100 p-6">
                <h4 className="font-bold text-indigo-900 mb-3">How Auto-Sync Works</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { step: '1', title: 'Configure Your DMS', desc: 'Set up a CSV export URL in your dealer software (Title Tec, DealerTrack, etc.)' },
                    { step: '2', title: 'Paste the URL Above', desc: 'Enter the export URL and choose your sync frequency' },
                    { step: '3', title: 'Automatic Updates', desc: 'Wakzy fetches new inventory automatically. Duplicates are skipped.' },
                  ].map(s => (
                    <div key={s.step} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center flex-shrink-0 text-sm">{s.step}</div>
                      <div>
                        <p className="font-semibold text-indigo-900 text-sm">{s.title}</p>
                        <p className="text-xs text-indigo-700 mt-0.5">{s.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== WEBHOOK ===== */}
      {activeView === 'webhook' && (
        <div className="space-y-6">
          {webhookLoading ? (
            <div className="text-center py-12"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* Webhook URL Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 bg-gradient-to-r from-orange-500 to-rose-500">
                  <h4 className="font-bold text-white flex items-center gap-2"><Webhook className="w-5 h-5" /> Real-Time Webhook</h4>
                  <p className="text-orange-100 text-sm mt-1">Your DMS pushes inventory changes to Wakzy instantly</p>
                </div>
                <div className="p-6 space-y-5">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1.5 block">Your Webhook URL</label>
                    <div className="flex gap-2">
                      <Input
                        value={webhookConfig?.webhook_url || ''}
                        readOnly
                        className="rounded-xl flex-1 font-mono text-xs bg-gray-50"
                        data-testid="webhook-url-display"
                      />
                      <Button
                        variant="outline"
                        className="rounded-xl"
                        onClick={() => copyToClipboard(webhookConfig?.webhook_url || '')}
                        data-testid="copy-webhook-btn"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Give this URL to your DMS provider. They will POST inventory data to it automatically.
                    </p>
                  </div>

                  <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Total Webhooks Received</p>
                      <p className="text-2xl font-bold text-orange-600">{webhookConfig?.total_received || 0}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                      onClick={handleRegenerateWebhook}
                      disabled={regenerating}
                      data-testid="regenerate-webhook-btn"
                    >
                      {regenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5 mr-1.5" />}
                      Regenerate Key
                    </Button>
                  </div>

                  {/* Accepted Formats */}
                  <div className="border-t border-gray-100 pt-4">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Accepted Payload Formats</h5>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-blue-500"></span>JSON
                        </p>
                        <pre className="text-xs text-gray-500 bg-white rounded-lg p-3 overflow-x-auto border border-gray-100">{`POST /api/inventory/webhook/push/{key}
Content-Type: application/json

{
  "vehicles": [
    {
      "make": "Honda",
      "model": "Civic",
      "year": 2021,
      "vin": "1HGBH41JXMN109186",
      "price": 22500,
      "mileage": 32000
    }
  ]
}`}</pre>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                          <span className="w-2 h-2 rounded-full bg-green-500"></span>CSV
                        </p>
                        <pre className="text-xs text-gray-500 bg-white rounded-lg p-3 overflow-x-auto border border-gray-100">{`POST /api/inventory/webhook/push/{key}
Content-Type: text/csv

stock_number,vin,year,make,model,...
STK001,1HGBH41JXMN109186,2021,Honda,Civic,...`}</pre>
                      </div>
                    </div>
                  </div>

                  {/* Security Note */}
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Security</p>
                      <p className="text-xs text-amber-600">Your webhook key is unique and secret. If compromised, click "Regenerate Key" to create a new one. The old URL will stop working immediately.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Webhook History */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h4 className="font-bold text-gray-900 flex items-center gap-2"><Clock className="w-5 h-5 text-gray-400" /> Recent Webhook Activity</h4>
                  <Button variant="ghost" size="sm" className="rounded-xl" onClick={fetchSyncHistory} data-testid="refresh-webhook-history-btn">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
                  </Button>
                </div>
                <div className="p-4">
                  {syncHistory.filter(h => h.source === 'webhook').length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Webhook className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p className="font-medium">No webhook pushes received yet</p>
                      <p className="text-sm">Configure your DMS to POST to the webhook URL above.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {syncHistory.filter(h => h.source === 'webhook').map((h, i) => (
                        <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3" data-testid={`webhook-history-${i}`}>
                          <div className="flex items-center gap-3">
                            {h.status === 'success' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {h.imported} imported, {h.duplicates} duplicates, {h.skipped} skipped
                              </p>
                              <p className="text-xs text-gray-400">
                                {h.started_at ? new Date(h.started_at).toLocaleString() : '-'}
                                <span className="ml-2 text-orange-500">Webhook</span>
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
